/**
 * Typed application error hierarchy
 * Makes error handling explicit and testable
 */

export class AppError extends Error {
  constructor(message, code, context = {}) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.context = context;
    this.timestamp = new Date().toISOString();
  }
}

export class AuthError extends AppError {
  constructor(message, context) {
    super(message, 'AUTH_ERROR', context);
    this.name = 'AuthError';
  }
}

export class SessionError extends AppError {
  constructor(message, context) {
    super(message, 'SESSION_ERROR', context);
    this.name = 'SessionError';
  }
}

export class SessionNotFoundError extends SessionError {
  constructor(code) {
    super(`Session '${code}' not found`, { code });
    this.name = 'SessionNotFoundError';
  }
}

export class SessionFullError extends SessionError {
  constructor(sessionId) {
    super('Session has reached maximum participants', { sessionId });
    this.name = 'SessionFullError';
  }
}

export class OCRError extends AppError {
  constructor(message, context) {
    super(message, 'OCR_ERROR', context);
    this.name = 'OCRError';
  }
}

export class NetworkError extends AppError {
  constructor(message, context) {
    super(message, 'NETWORK_ERROR', context);
    this.name = 'NetworkError';
  }
}

export class StorageError extends AppError {
  constructor(message, context) {
    super(message, 'STORAGE_ERROR', context);
    this.name = 'StorageError';
  }
}

/**
 * Map Supabase error codes to typed AppErrors
 */
export const fromSupabaseError = (error, context = {}) => {
  if (!error) return null;
  const msg = error.message || 'Unknown error';
  const code = error.code || '';

  if (code === 'PGRST116' || msg.includes('not found')) return new SessionNotFoundError(context.code);
  if (msg.includes('JWT') || msg.includes('auth')) return new AuthError(msg, context);
  if (msg.includes('network') || error.name === 'FetchError') return new NetworkError(msg, context);
  return new AppError(msg, code, context);
};

/**
 * User-friendly messages for each error type
 */
export const getUserMessage = (error) => {
  if (error instanceof SessionNotFoundError) return 'Session not found. Check your code and try again.';
  if (error instanceof SessionFullError)     return 'This session is full.';
  if (error instanceof AuthError)            return 'Authentication failed. Please sign in again.';
  if (error instanceof OCRError)             return 'Could not read the receipt. Try a clearer photo.';
  if (error instanceof NetworkError)         return 'No internet connection. Changes will sync when you\'re back online.';
  if (error instanceof StorageError)         return 'Could not upload the receipt image.';
  return 'Something went wrong. Please try again.';
};
